import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Eye, EyeOff, KeyRound, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Secrets du fichier .env d'un projet.
 *
 * Cet éditeur n'existait que sur la version mobile : il avait été écrit pendant
 * le travail sur la PWA et jamais porté sur le cockpit. L'onglet « Accès » du
 * bureau, lui, ne montre que des notes stockées en base — pas le vrai fichier
 * qui fait tourner l'application.
 *
 * On ne relit jamais une valeur en clair depuis le serveur : l'API ne renvoie
 * que les noms des variables et un aperçu tronqué. Écrire une valeur la
 * remplace, laisser le champ vide la conserve.
 */

const MODELES = [
  { titre: "Stripe", cles: ["STRIPE_SECRET_KEY", "STRIPE_PUBLISHABLE_KEY", "STRIPE_WEBHOOK_SECRET"] },
  { titre: "Envoi de mail (SMTP)", cles: ["SMTP_HOST", "SMTP_PORT", "SMTP_USER", "SMTP_PASSWORD", "EMAIL_FROM"] },
  { titre: "Base de données", cles: ["MONGO_URL", "DB_NAME"] },
];

export default function SecretsEnv({
  projectId,
  token,
  route,              // URL complète, pour les secrets qui ne dépendent d'aucun projet
  titre = "Secrets du fichier .env",
  modeles = MODELES,
  avecRedemarrage = true,
  aide,
}) {
  const [donnees, setDonnees] = useState(null);
  const [saisies, setSaisies] = useState({});
  const [libres, setLibres] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [redemarrer, setRedemarrer] = useState(true);
  const [devoile, setDevoile] = useState({});

  const entetes = { Authorization: `Bearer ${token}` };
  const url = route || `${API_URL}/api/projects/${projectId}/env`;

  const charger = async () => {
    try {
      const r = await fetch(url, { headers: entetes });
      if (r.ok) setDonnees(await r.json());
      else setDonnees({ path: "", exists: false, vars: [] });
    } catch {
      setDonnees({ path: "", exists: false, vars: [] });
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    if (token) charger();
    // eslint-disable-next-line
  }, [projectId, token]);

  const modifier = (cle, valeur) => setSaisies((s) => ({ ...s, [cle]: valeur }));

  const enregistrer = async () => {
    const vars = [
      ...Object.entries(saisies),
      ...libres.filter((l) => l.key.trim()).map((l) => [l.key.trim(), l.value]),
    ]
      .filter(([, v]) => v && String(v).trim())
      .map(([key, value]) => ({ key, value: String(value).trim() }));

    if (!vars.length) {
      toast.error("Aucune valeur à enregistrer");
      return;
    }
    setEnvoi(true);
    try {
      const r = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...entetes },
        body: JSON.stringify({ vars, restart: avecRedemarrage && redemarrer }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "échec de l'enregistrement");
      setSaisies({});
      setLibres([]);
      toast.success(
        `${d.saved.length} variable(s) enregistrée(s)` +
          (d.restart ? (d.restart.code === 0 ? " · service redémarré" : " · redémarrage en échec") : "")
      );
      await charger();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-2 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des secrets…
      </div>
    );
  }

  const connues = donnees?.vars || [];
  const aSaisir =
    Object.values(saisies).some((v) => v && String(v).trim()) ||
    libres.some((l) => l.key.trim() && String(l.value).trim());

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-bold text-slate-900 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-indigo-600" />
          {titre}
        </h2>
        {donnees?.path && (
          <code className="text-xs text-slate-400 font-mono truncate max-w-[46%]">{donnees.path}</code>
        )}
      </div>

      {!donnees?.path && !route ? (
        <p className="text-sm text-slate-500 mt-3">
          Ce projet n'a pas de dossier serveur déclaré. Renseignez <b>folder_path</b> sur la fiche
          du projet pour pouvoir y déposer des secrets.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-500 mt-1 mb-5">
            {aide ||
              (donnees.exists
                ? `${connues.length} variable(s) déjà en place. Les valeurs ne sont jamais relues en clair : laisser un champ vide conserve la valeur actuelle.`
                : "Le fichier sera créé au premier enregistrement.")}
          </p>

          {connues.length > 0 && (
            <div className="mb-6 rounded-lg border border-slate-200 divide-y divide-slate-100">
              {connues.map((v) => (
                <div key={v.key} className="flex items-center gap-3 px-3 py-2">
                  <code className="text-xs font-mono text-slate-700 flex-1 truncate">{v.key}</code>
                  <code className="text-xs font-mono text-slate-400">
                    {devoile[v.key] ? v.preview : "••••••••"}
                  </code>
                  <button
                    type="button"
                    onClick={() => setDevoile((d) => ({ ...d, [v.key]: !d[v.key] }))}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label="Afficher l'aperçu"
                  >
                    {devoile[v.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {modeles.map((m) => (
            <div key={m.titre} className="mb-5">
              <p className="text-sm font-semibold text-slate-700 mb-2">{m.titre}</p>
              <div className="grid gap-2 md:grid-cols-2">
                {m.cles.map((cle) => (
                  <div key={cle}>
                    <label className="block text-xs font-mono text-slate-500 mb-1">
                      {cle}
                      {connues.some((v) => v.key === cle) && (
                        <span className="ml-2 text-emerald-600 font-sans">déjà en place</span>
                      )}
                    </label>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={saisies[cle] || ""}
                      onChange={(e) => modifier(cle, e.target.value)}
                      placeholder="laisser vide pour ne pas changer"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-700">Autre variable</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLibres((l) => [...l, { key: "", value: "" }])}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
              </Button>
            </div>
            {libres.map((l, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-2 mb-2">
                <Input
                  value={l.key}
                  onChange={(e) =>
                    setLibres((ls) => ls.map((x, k) => (k === i ? { ...x, key: e.target.value } : x)))
                  }
                  placeholder="NOM_DE_LA_VARIABLE"
                  className="font-mono text-sm"
                />
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={l.value}
                  onChange={(e) =>
                    setLibres((ls) => ls.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))
                  }
                  placeholder="valeur"
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            {avecRedemarrage ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={redemarrer}
                  onChange={(e) => setRedemarrer(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Redémarrer le service après enregistrement
              </label>
            ) : (
              <span className="text-sm text-slate-400">Lus à la demande, aucun redémarrage nécessaire.</span>
            )}
            <Button onClick={enregistrer} disabled={!aSaisir || envoi}>
              {envoi ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
